import { useCallback, useEffect } from 'react';

import { resolveNectarPageBlock } from '~/utils/nectarPageBlock';

import { useNectarAnalyticsContext } from './useNectarAnalyticsContext';

// Enum for click types - simplified to match actual detection logic
export enum ClickType {
  // Basic HTML elements
  BUTTON = 'button',
  LINK = 'link',
  INPUT = 'input',
  SELECT = 'select',
  TEXTAREA = 'textarea',
  LABEL = 'label',
  SUMMARY = 'summary',
  IMG = 'img',
  SPAN = 'span',
  SVG = 'svg',
  P = 'p',

  // Heading elements
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  H5 = 'h5',
  H6 = 'h6',

  // Structural elements
  DIV = 'div',
  SECTION = 'section',
  ARTICLE = 'article',
  HEADER = 'header',
  FOOTER = 'footer',
  NAV = 'nav',
  MAIN = 'main',
  ASIDE = 'aside',
  BODY = 'body',
  HTML = 'html',

  // Form elements
  FORM = 'form',
  FIELDSET = 'fieldset',
  LEGEND = 'legend',

  // List elements
  UL = 'ul',
  OL = 'ol',
  LI = 'li',

  // Table elements
  TABLE = 'table',
  TR = 'tr',
  TD = 'td',
  TH = 'th',

  // Input types
  SUBMIT = 'submit',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',

  // Special link types
  EMAIL = 'email',
  PHONE = 'phone',

  // Text formatting elements
  U = 'u',
  STRONG = 'strong',
  B = 'b',
  EM = 'em',
  I = 'i',

  // SVG elements
  PATH = 'path',

  // Fallback for unknown elements
  UNKNOWN = 'unknown',
}

// Utility functions moved outside component to avoid recreation on every render
const getClickType = (element: HTMLElement): string => {
  const tagName = element.tagName.toLowerCase();

  // Check for specific input types
  if (tagName === 'input') {
    const type = element.getAttribute('type');
    if (type === 'submit') return ClickType.SUBMIT;
    if (type === 'button') return ClickType.BUTTON;
    if (type === 'checkbox') return ClickType.CHECKBOX;
    if (type === 'radio') return ClickType.RADIO;
    return ClickType.INPUT;
  }

  // Check for links with special protocols
  if (tagName === 'a') {
    const href = element.getAttribute('href');
    if (href) {
      if (href.startsWith('mailto:')) return ClickType.EMAIL;
      if (href.startsWith('tel:')) return ClickType.PHONE;
    }
    return ClickType.LINK;
  }

  // Check if element is inside a link (for nested elements like spans inside <a> tags)
  const parentLink = element.closest('a');
  if (parentLink) {
    const href = parentLink.getAttribute('href');
    if (href) {
      if (href.startsWith('mailto:')) return ClickType.EMAIL;
      if (href.startsWith('tel:')) return ClickType.PHONE;
    }
    return ClickType.LINK;
  }

  // Simple tag-based mapping
  if (tagName === 'button') return ClickType.BUTTON;
  if (tagName === 'select') return ClickType.SELECT;
  if (tagName === 'textarea') return ClickType.INPUT;
  if (tagName === 'label') return ClickType.INPUT;
  if (tagName === 'summary') return ClickType.SUMMARY;
  if (tagName === 'img') return ClickType.IMG;
  if (tagName === 'span') return ClickType.SPAN;
  if (tagName === 'svg') return ClickType.SVG;
  if (tagName === 'p') return ClickType.P;

  // Heading elements
  if (tagName === 'h1') return ClickType.H1;
  if (tagName === 'h2') return ClickType.H2;
  if (tagName === 'h3') return ClickType.H3;
  if (tagName === 'h4') return ClickType.H4;
  if (tagName === 'h5') return ClickType.H5;
  if (tagName === 'h6') return ClickType.H6;

  // Structural elements
  if (tagName === 'div') return ClickType.DIV;
  if (tagName === 'section') return ClickType.SECTION;
  if (tagName === 'article') return ClickType.ARTICLE;
  if (tagName === 'header') return ClickType.HEADER;
  if (tagName === 'footer') return ClickType.FOOTER;
  if (tagName === 'nav') return ClickType.NAV;
  if (tagName === 'main') return ClickType.MAIN;
  if (tagName === 'aside') return ClickType.ASIDE;
  if (tagName === 'body') return ClickType.BODY;

  // Form elements
  if (tagName === 'form') return ClickType.FORM;
  if (tagName === 'fieldset') return ClickType.FIELDSET;
  if (tagName === 'legend') return ClickType.LEGEND;

  // List elements
  if (tagName === 'ul') return ClickType.UL;
  if (tagName === 'ol') return ClickType.OL;
  if (tagName === 'li') return ClickType.LI;

  // Table elements
  if (tagName === 'table') return ClickType.TABLE;
  if (tagName === 'tr') return ClickType.TR;
  if (tagName === 'td') return ClickType.TD;
  if (tagName === 'th') return ClickType.TH;

  // Document elements
  if (tagName === 'body') return ClickType.BODY;
  if (tagName === 'html') return ClickType.HTML;

  // Text formatting elements
  if (tagName === 'u') return ClickType.U;
  if (tagName === 'strong') return ClickType.STRONG;
  if (tagName === 'b') return ClickType.B;
  if (tagName === 'em') return ClickType.EM;
  if (tagName === 'i') return ClickType.I;

  // SVG elements
  if (tagName === 'path') return ClickType.PATH;

  // Return UNKNOWN for unhandled elements
  return ClickType.UNKNOWN;
};

const buildUrl = (element: HTMLElement, attribute: string): string | undefined => {
  const hrefOrAction = element.getAttribute(attribute);
  if (hrefOrAction) {
    // Convert relative URLs to absolute
    if (hrefOrAction.startsWith('/')) {
      return window.location.origin + hrefOrAction;
    }
    if (hrefOrAction.startsWith('#')) {
      return window.location.href + hrefOrAction;
    }
    if (hrefOrAction.startsWith('http')) {
      return hrefOrAction;
    }
  }
  return undefined;
};

const getClickUrl = (element: HTMLElement): string | undefined => {
  if (element.tagName.toLowerCase() === 'a') {
    return buildUrl(element, 'href');
  }
  const parentLink = element.closest('a');
  if (parentLink) {
    return buildUrl(parentLink, 'href');
  }
  if (element.tagName.toLowerCase() === 'form') {
    return buildUrl(element, 'action');
  }
  return undefined;
};

// Elements (and their descendants) carrying this attribute are excluded from
// automatic click tracking — used to keep dev tooling out of analytics data.
export const NECTAR_NO_TRACK_ATTR = 'data-nectar-no-track';

// Hook for automatically tracking click events
export const useNectarClickTracking = (): void => {
  const analytics = useNectarAnalyticsContext();

  // Global click handler for automatic tracking
  const handleGlobalClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Skip clicks inside opted-out subtrees (e.g. the dev analytics console).
      if (target.closest(`[${NECTAR_NO_TRACK_ATTR}]`)) return;

      const clickType = getClickType(target);
      const clickUrl = getClickUrl(target);
      const clickRawTagName = target.tagName.toLowerCase();
      const pageBlock = resolveNectarPageBlock(target);

      analytics.trackClick({ clickType, clickUrl, clickRawTagName, pageBlock });
    },
    [analytics],
  );

  // Set up global click listener
  useEffect(() => {
    document.addEventListener('click', handleGlobalClick, true);

    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [handleGlobalClick]);
};
